import styled from 'styled-components'

import { Box } from './UI/Box'
import { ThemedButton } from './ThemedButton'

const Dots = styled(Box)`
  min-width: 52px;
  height: 48px;
  opacity: 0.5;
  justify-content: center;
  align-items: center;
  flex: 0;
  margin-right: 16px;
  margin-left: 18px;
`

const DotsText = styled.span`
  font-size: 28px;
  height: 48px;
  line-height: 30px;
  letter-spacing: 8px;
`

const Container = styled(Box)`
  position: relative;
  align-self: center;
  flex: 1;
  width: 100%;
  @media screen and (max-width: 600px) {
    flex-direction: column-reverse;
  }
`

const PrevNextContainer = styled(Box)`
  @media screen and (max-width: 600px) {
    flex: 1;
    width: 100%;
    justify-content: flex-start;
    margin-bottom: 32px;
  }
`

const PageNumbers = styled(Box)`
  @media screen and (max-width: 600px) {
    justify-content: center;
  }
`

type Props = {
  total: number
  limit: number
  offset: number
  onNext: () => void
  onPrev: () => void
  goToPage: (page: number) => void
}

const GROUP_MAX = 3
const half = Math.ceil(GROUP_MAX / 2)

export default function Pagination({
  total,
  limit,
  offset,
  onNext,
  onPrev,
  goToPage,
}: Props) {
  const currentPage = Math.floor(offset / limit)
  const pages = Math.ceil(total / limit)
  const lastPage = pages - 1

  const getButton = (page: number) => (
    <ThemedButton
      key={`tab-${page}`}
      $styleType={currentPage === page ? 'primary' : 'tertiary'}
      onClick={() => goToPage(page)}
    >
      {page + 1}
    </ThemedButton>
  )

  return (
    <Container justify="space-between" align="flex-start">
      <PageNumbers g={6}>
        {pages <= GROUP_MAX ? (
          Array(pages > 0 ? pages : 0)
            .fill(0)
            .map((item, index) => getButton(index))
        ) : (
          <>
            {getButton(0)}
            {currentPage > 0 + half && (
              <Dots>
                <DotsText>...</DotsText>
              </Dots>
            )}
            {Array(GROUP_MAX)
              .fill(0)
              .map((item, index) => {
                const p = currentPage + 1 - half + index
                return p > 0 && p < lastPage ? getButton(p) : ''
              })}
            {currentPage < pages - half && (
              <Dots>
                <DotsText>...</DotsText>
              </Dots>
            )}
            {getButton(lastPage)}
          </>
        )}
      </PageNumbers>
      <PrevNextContainer justify="flex-end" g={6}>
        {currentPage > 0 && (
          <ThemedButton $styleType="tertiary" onClick={onPrev}>
            Prev
          </ThemedButton>
        )}
        {currentPage < lastPage && (
          <ThemedButton $styleType="tertiary" onClick={onNext}>
            Next
          </ThemedButton>
        )}
      </PrevNextContainer>
    </Container>
  )
}
